import React, { Component } from 'react'
import './Table.css'
import classnames from 'classnames'
import { AllCommunityModule } from 'ag-grid-community'
import { AgGridProvider, AgGridReact } from 'ag-grid-react'
import { getLocaleText } from './utils'

const modules = [AllCommunityModule]

export const HEADER_HEIGHT = 50
export const ROW_HEIGHT = 50
export const BORDER_WIDTH = 1

const tableHeightForRows = nRows => ROW_HEIGHT * nRows

// Lines flagged by the entity "_pinned" getter (lines the connected user has to treat).
const isPinned = data => data?._pinned === true

// Keeps the pinned lines at the top, after any column sort / filter. They stay regular rows
// (searchable, filterable, selectable, counted), ordered among themselves by the active sort.
// AG Grid calls this on every sort pass, even without an active sort, and expects in-place changes.
const pinRowsFirst = ({ nodes }) => {
    const pinned = nodes.filter(node => isPinned(node.data))
    if (!pinned.length || pinned.length === nodes.length) return
    const others = nodes.filter(node => !isPinned(node.data))
    nodes.splice(0, nodes.length, ...pinned, ...others)
}

export class Table extends Component {

    sortActive = false
    filterActive = false
    gridApi = null
    clickTimeout = null
    containerRef = React.createRef()

    componentDidMount() {
        document.addEventListener('click', this.handleOutsideClick)
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleOutsideClick)
    }

    handleOutsideClick = (event) => {
        if (!this.gridApi) return
        const path = event.composedPath?.() ?? []
        // composedPath preserves the original event path even when elements are removed from DOM
        // (e.g. react-phone-input-2 closes its dropdown before the click handler runs)
        if (path.includes(this.containerRef.current)) return
        // react-select menus are rendered in a document.body portal — check by class
        if (path.some(el => el?.classList?.contains('ag-select__menu'))) return
        // AG Grid popups (filter, column menu) are rendered in popupParent — keep them open
        if (path.some(el => el?.classList?.contains('ag-popup'))) return
        this.gridApi.stopEditing()
    }

    componentDidUpdate(prevProps) {
        const { editedRowId } = this.props
        if (!this.gridApi) return;
        if (editedRowId) {
            const node = this.gridApi.getRowNode(String(editedRowId));
            if (node) {
                this.gridApi.ensureNodeVisible(node, 'top');
            }
        }
    }

    getRowSelection = () => {
        if (!this.props.enableSelection) return undefined
        return { mode: 'multiRow' }
    }

    onGridReady = (params) => {
        this.gridApi = params.api

        const { getInitialFilterModel } = this.props
        const initialFilterModel = getInitialFilterModel?.()
        if (initialFilterModel && Object.keys(initialFilterModel).length > 0) {
            params.api.setFilterModel(initialFilterModel)
        }

        if (!this.props.enableSelection) return

        const { objects } = this.props
        objects?.forEach(obj => {
            if (obj.selected) {
                const node = params.api.getRowNode(String(obj.id))
                node?.setSelected(true)
            }
        })
    }

    computeHeight = () => {
        const totalHeight = tableHeightForRows(this.props.objects?.length || 0)
        if (this.props.autoGrow) return totalHeight

        const maxHeight = this.props.maxRows && tableHeightForRows(this.props.maxRows)
        const height = this.props.maxRows
            ? Math.min(totalHeight, maxHeight)
            : totalHeight

        const headerHeight = this.props.disableHeader ? 0 : HEADER_HEIGHT

        return Math.max(
            height + headerHeight + 3 * BORDER_WIDTH,
            HEADER_HEIGHT + ROW_HEIGHT + 3 * BORDER_WIDTH
        )
    }

    getHeight = () => this.props.height || this.computeHeight()

    getRowClass = () => {
        return classnames(
            'AgGrid-Table-row',
            this.props.rowClassName,
            this.props.headerRowClassName
        )
    }

    getRowClassRules = () => {
        const { editedRowId, onRowClick, noHover } = this.props
        return {
            editable: ({ data }) => !!onRowClick && !data?.noFormAccess,
            header: ({ rowIndex }) => rowIndex === -1,
            first: ({ rowIndex }) => rowIndex === 0,
            hover: ({ data }) => !noHover && !!onRowClick && !data?.noFormAccess,

            greenStyledRow: ({ data }) =>
                data && !data?.orangeStyledRow && data?.greenStyledRow,

            orangeStyledRow: ({ data }) => data?.orangeStyledRow,
            redStyledRow: ({ data }) => data?.redStyledRow,
            pinnedRow: ({ data }) => isPinned(data),
            saving: ({ data }) => !!data.saving,
            highlighted: ({ data }) => data?.id === editedRowId

        }
    }

    onSortChanged = () => {
        const api = this.gridApi
        const colState = api.getColumnState() || []

        this.sortActive = colState.some(c => c.sort)
        api.setGridOption("suppressRowDrag", this.sortActive || this.filterActive)

        if (this.props.onSortChanged) {
            const sorted = colState.find(c => c.sort)
            this.props.onSortChanged(
                sorted ? { sortBy: sorted.colId, sortDirection: sorted.sort } : { sortBy: null, sortDirection: null }
            )
        }
    }

    onFilterChanged = () => {
        const api = this.gridApi

        this.filterActive = api.isAnyFilterPresent()
        api.setGridOption("suppressRowDrag", this.sortActive || this.filterActive)

        if (this.props.onFilterChanged) {
            this.props.onFilterChanged(api.getFilterModel())
        }
    }

    onModelUpdated = () => {
        if (this.props.onDisplayedRowCountChanged) {
            this.props.onDisplayedRowCountChanged(this.gridApi.getDisplayedRowCount())
        }
    }

    onRowDragMove = (event) => {
        const { node: movingNode, overNode } = event
        if (!overNode) return

        if (movingNode !== overNode) {
            this.props.moveRow(movingNode.data, overNode.data)
            this.gridApi.clearFocusedCell()
        }
    }

    handleCellValueChanged = async (params) => {
        const { rowIndex, data, colDef, oldValue, node } = params

        try {
            const result = await this.props.onChange({rowIndex, object: data})

            if (result?.error) {
                node.setData({ ...data, [colDef.dataKey]: oldValue })
            } else {
                this.props.setEditedRowId(data.id)
            }
        } catch (e) {
            node.setData({ ...data, [colDef.dataKey]: oldValue })
        }
    }

    onCellClicked = (event) => {
        const editingCells = this.gridApi.getEditingCells();

        if (editingCells.length > 0) return;
        if (this.clickTimeout) clearTimeout(this.clickTimeout);

        this.clickTimeout = setTimeout(() => {
            this.props.onRowClick?.(event)
        }, 250)
    };

    onCellDoubleClicked = (event) => {
        if (this.clickTimeout) clearTimeout(this.clickTimeout);

        this.gridApi.startEditingCell({
            rowIndex: event.node.rowIndex,
            colKey: event.column.getColId(),
        });
    };

    render() {
        const { columns, objects, enableSelection, enableRowDragging, t } = this.props

        const finalColumnDefs = enableRowDragging
            ? columns.map((col, index) =>
                index === 0 ? { ...col, rowDrag: true } : col
            )
            : columns

        return (
            <AgGridProvider modules={modules}>
                <div
                    ref={this.containerRef}
                    style={{
                        height: `${this.getHeight()}px`,
                        width: this.props.width ? `${this.props.width}px` : '100%'
                    }}
                >
                    <AgGridReact
                        rowData={objects}
                        columnDefs={finalColumnDefs}
                        popupParent={typeof document !== 'undefined' ? document.body : undefined}
                        groupHeaderHeight={HEADER_HEIGHT}
                        headerHeight={HEADER_HEIGHT}
                        rowHeight={ROW_HEIGHT}
                        rowSelection={this.getRowSelection()}
                        getRowId={(params) => String(params.data.id)}

                        singleClickEdit={false}
                        suppressClickEdit={true}

                        onCellClicked={this.onCellClicked}
                        onCellDoubleClicked={this.onCellDoubleClicked}

                        getRowClass={this.getRowClass}
                        rowClassRules={this.getRowClassRules()}
                        postSortRows={pinRowsFirst}

                        onGridReady={this.onGridReady}
                        onRowSelected={enableSelection ? this.props.onRowSelected : undefined}
                        onSortChanged={this.onSortChanged}
                        onFilterChanged={this.onFilterChanged}
                        onModelUpdated={this.onModelUpdated}
                        onColumnResized={this.props.onColumnResized}
                        onRowDragMove={enableRowDragging ? this.onRowDragMove : undefined}
                        onCellValueChanged={this.handleCellValueChanged}

                        stopEditingWhenCellsLoseFocus={false}
                        suppressCellFocus={true}

                        enableBrowserTooltips={true}
                        defaultColDef={{
                            tooltipValueGetter: (p) => {
                                const v = p.valueFormatted ?? p.value
                                return typeof v === 'string' || typeof v === 'number' ? String(v) : undefined
                            }
                        }}

                        localeText={getLocaleText(t)}
                    />
                </div>
            </AgGridProvider>
        )
    }
}

export default Table
